// src/hr/portal/ProfileRecord.js -- the profile's "Record" tab (spec §7.2, §8.4, §16.2 M3/M5):
// edit the record and status / last day; archive (confirmed in the page); legal hold; the owner-only
// permanent delete, offered only once the person is archived, past their retention date and not on
// legal hold, with their name typed to confirm; and the rehire prompt that restores unused sick hours.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiArchive, FiCheck, FiLock, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, Field, Seg } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel } from '../format';
import EmailChips, { withDraft } from './EmailChips';
import { EMAIL_RE, EMPLOYMENT_TYPES, fullDate, fullName, isCurrent } from './portalUtils';
import { dateOfTimestamp } from '../timeoff/dates';

const STATUSES = [{ value: 'active', label: 'Active' }, { value: 'leaving', label: 'Leaving' }, { value: 'left', label: 'Left' }];
const norm = (s) => String(s || '').trim();
const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

function initialForm(p) {
  const primary = String(p.email || '').toLowerCase();
  return {
    firstName: p.firstName || '',
    lastName: p.lastName || '',
    email: p.email || '',
    emails: (p.emails || []).filter((e) => e !== primary),
    title: p.title || '',
    department: p.department || '',
    hireDate: p.hireDate || '',
    employmentType: p.employmentType || 'full-time',
    managerId: p.managerId || '',
    status: p.status || 'active',
    lastDay: p.lastDay || ''
  };
}

// Only the fields that changed, in the shape PATCH /admin/people/:id takes ('' clears).
function changesOf(form, start, emails) {
  const body = {};
  ['firstName', 'lastName', 'email', 'title', 'department'].forEach((key) => {
    if (norm(form[key]) !== norm(start[key])) body[key] = norm(form[key]);
  });
  ['hireDate', 'employmentType', 'managerId', 'status', 'lastDay'].forEach((key) => {
    if ((form[key] || '') !== (start[key] || '')) body[key] = form[key] || null;
  });
  if (!sameList(emails, start.emails)) body.emails = emails;
  return body;
}

function RecordForm({ person, people, onSaved }) {
  // Keyed on the values, so an unrelated update (legal hold, archive) never wipes edits in progress.
  const startKey = JSON.stringify(initialForm(person));
  const start = useMemo(() => JSON.parse(startKey), [startKey]);
  const [form, setForm] = useState(start);
  const [draft, setDraft] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const statusId = `hrp-rec-${plainId(useId())}`;

  // A changed record (after a save or reload) resets the form; the first render already has it.
  const shownKey = useRef(startKey);
  useEffect(() => {
    if (shownKey.current === startKey) return;
    shownKey.current = startKey;
    setForm(start);
    setDraft('');
    setErrors({});
  }, [startKey, start]);

  // Active people other than this person; a manager who has since left stays listed so the form
  // shows the saved value.
  const managers = useMemo(() => {
    const list = (people || [])
      .filter((p) => isCurrent(p) && p.id !== person.id)
      .map((p) => ({ value: p.id, label: fullName(p) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (person.managerId && !list.some((m) => m.value === person.managerId)) {
      const name = person.managerName || 'Current manager';
      list.push({ value: person.managerId, label: people ? `${name} (no longer here)` : name });
    }
    return list;
  }, [people, person.id, person.managerId, person.managerName]);

  const set = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const extra = withDraft(form.emails, draft, form.email);
  const body = changesOf(form, start, extra.emails);
  const dirty = Object.keys(body).length > 0;

  const save = async () => {
    if (busy) return;
    const found = {};
    if (!norm(form.firstName)) found.firstName = 'Enter a first name';
    if (!norm(form.lastName)) found.lastName = 'Enter a last name';
    if (!EMAIL_RE.test(norm(form.email))) found.email = 'Enter a valid work email';
    if (extra.error) found.emails = extra.error;
    if (form.status === 'leaving' && !form.lastDay) found.lastDay = 'Set a last day first';
    if (form.hireDate && form.lastDay && form.lastDay < form.hireDate) found.lastDay = 'Last day is before the hire date';
    setErrors(found);
    if (Object.keys(found).length || !dirty) return;
    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.people.update(person.id, body);
      setBusy(false);
      onSaved(result || {}, body);
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't save the record. Try again."));
    }
  };

  return (
    <Card delay={1} as="section" aria-label="Record" title="Record" className="hrp-rec">
      <form className="hrp-form" noValidate onSubmit={(e) => { e.preventDefault(); save(); }}>
        <div className="hrp-two">
          <Field label="First name" value={form.firstName} onChange={set('firstName')} error={errors.firstName} maxLength={80} />
          <Field label="Last name" value={form.lastName} onChange={set('lastName')} error={errors.lastName} maxLength={80} />
        </div>
        <Field label="Work email" type="email" value={form.email} onChange={set('email')} error={errors.email} hint="Shown in the portal. Any of their addresses links their sign-in." />
        <EmailChips emails={form.emails} onChange={set('emails')} draft={draft} onDraftChange={setDraft} primary={form.email} error={errors.emails} />
        <div className="hrp-two">
          <Field label="Title" value={form.title} onChange={set('title')} maxLength={100} />
          <Field label="Department" value={form.department} onChange={set('department')} maxLength={100} />
        </div>
        <div className="hrp-two">
          <Field label="Hire date" type="date" value={form.hireDate} onChange={set('hireDate')} />
          <Field label="Employment type" type="select" value={form.employmentType} onChange={set('employmentType')} options={EMPLOYMENT_TYPES} />
        </div>
        <Field label="Manager" type="select" value={form.managerId} onChange={set('managerId')} options={[{ value: '', label: 'No manager' }, ...managers]} />

        <div className="hrp-rec__status">
          <div className="hrp-field">
            <div className="hrp-field__label" id={statusId}>Status</div>
            <Seg labelledBy={statusId} options={STATUSES} value={form.status} onChange={set('status')} />
          </div>
          {form.status !== 'active' && (
            <Field
              label="Last day"
              type="date"
              value={form.lastDay}
              onChange={set('lastDay')}
              error={errors.lastDay}
              hint={form.status === 'leaving' ? 'Saving offers to start the offboarding checklist.' : undefined}
            />
          )}
        </div>
        {start.status === 'left' && form.status !== 'left' && (
          <p className="hrp-rec__note">Bringing them back records a rehire. Within 6 months of their last day you can restore their unused Sick &amp; Safe hours.</p>
        )}

        {error && <div className="hrp-form-err" role="alert"><FiAlertCircle aria-hidden="true" /><span>{error}</span></div>}
        <div className="hrp-rec__foot">
          <Btn variant="ghost" size="sm" onClick={() => { setForm(start); setDraft(''); setErrors({}); setError(''); }} disabled={!dirty || busy}>Reset</Btn>
          <Btn type="submit" size="sm" icon={FiCheck} loading={busy} disabled={!dirty}>Save changes</Btn>
        </div>
      </form>
    </Card>
  );
}

function RehirePrompt({ person, hours, onRestored }) {
  const [value, setValue] = useState(String(hours));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const id = `hrp-rehire-${plainId(useId())}`;
  const n = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(n) && n > 0 && n <= hours;

  const restore = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.people.restoreSick(person.id, { hours: n });
      onRestored((result && result.restoredHours) || n);
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't restore the hours. Try again."));
    }
  };

  return (
    <section className="hrp-banner hrp-banner--info hrp-rehire" aria-labelledby={`${id}-t`}>
      <span className="hrp-banner__ic" aria-hidden="true"><FiRotateCcw /></span>
      <div className="hrp-banner__txt">
        <h2 className="hrp-banner__title" id={`${id}-t`}>Restore unused Sick &amp; Safe hours</h2>
        <p className="hrp-banner__text">
          {`Rehired within 6 months of their last day. They had ${hours} hours (${daysLabel(hours)}) of Sick & Safe unused when they left — NJ law gives it back.`}
        </p>
        <div className="hrp-rehire__row">
          <Field
            label="Hours to restore"
            type="number"
            min={1}
            max={hours}
            step={1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={value.trim() !== '' && !valid ? `Restore between 1 and ${hours} hours` : undefined}
            className="hrp-rehire__field"
          />
          <Btn icon={FiRotateCcw} loading={busy} disabled={!valid} onClick={restore}>{`Restore ${valid ? n : hours} hours`}</Btn>
        </div>
        {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
      </div>
    </section>
  );
}

function OffboardingOffer({ person, onStarted, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const start = async () => {
    setBusy(true);
    setError('');
    try {
      await hr.admin.checklists.start(person.id, { kind: 'offboarding' });
      onStarted();
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't start the checklist. Try again."));
    }
  };
  return (
    <section className="hrp-banner hrp-offb" aria-label="Start offboarding">
      <span className="hrp-banner__ic" aria-hidden="true"><FiArchive /></span>
      <div className="hrp-banner__txt">
        <div className="hrp-banner__title">{`${fullName(person)} is leaving${person.lastDay ? ` on ${fullDate(person.lastDay)}` : ''}`}</div>
        <div className="hrp-banner__text">Start the offboarding checklist: final balance, payroll, access, badge and laptop.</div>
        {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
      </div>
      <div className="hrp-offb__btns">
        <Btn variant="ghost" size="sm" onClick={onDismiss} disabled={busy}>Not now</Btn>
        <Btn size="sm" loading={busy} onClick={start}>Start offboarding checklist</Btn>
      </div>
    </section>
  );
}

function RetentionCard({ person, me, today, onArchived, onHold, onDeleted }) {
  const name = fullName(person);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const ids = `hrp-ret-${plainId(useId())}`;
  const keepRef = useRef(null);
  const typedRef = useRef(null);

  useEffect(() => { if (confirmArchive && keepRef.current) keepRef.current.focus(); }, [confirmArchive]);
  useEffect(() => { if (deleting && typedRef.current) typedRef.current.focus(); }, [deleting]);

  const run = async (kind, call, after) => {
    setBusy(kind);
    setError('');
    try {
      const result = await call();
      setBusy(null);
      after(result || {});
    } catch (err) {
      setBusy(null);
      setError(errorText(err, "Couldn't save that. Try again."));
    }
  };

  const archived = !!person.archivedAt;
  const pastRetention = !!person.retainUntil && person.retainUntil < today;
  const canPurge = !!me.isOwner && archived && pastRetention && !person.legalHold;
  let ownerNote = null;
  if (me.isOwner && archived && !canPurge) {
    ownerNote = person.legalHold
      ? 'It can’t be deleted permanently while legal hold is on.'
      : `Permanent delete is possible after ${fullDate(person.retainUntil) || 'the retention date'}.`;
  }

  return (
    <Card delay={2} as="section" aria-label="Records and retention" title="Records & retention" className="hrp-ret">
      <div className="hrp-ret__sec">
        {archived ? (
          <p className="hrp-ret__txt">
            <FiArchive aria-hidden="true" />
            {`Archived ${fullDate(dateOfTimestamp(person.archivedAt)) || ''}. Records are kept until ${fullDate(person.retainUntil) || 'the retention date'}.`}
          </p>
        ) : (
          <>
            <p className="hrp-ret__txt">
              Archiving keeps their records for 6 years after their last day (NJ) and takes them off People and Who's Out. Nothing is deleted.
            </p>
            {!confirmArchive && (
              <Btn variant="danger" size="sm" icon={FiArchive} onClick={() => setConfirmArchive(true)}>Archive person</Btn>
            )}
            {confirmArchive && (
              <div className="hrp-tl__confirm hrp-ret__confirm" role="group" aria-label={`Archive ${name}?`}>
                <span>{`Archive ${name}? Their status becomes Left.`}</span>
                <span className="hrp-tl__btns">
                  <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setConfirmArchive(false)} disabled={busy === 'archive'}>Keep</Btn>
                  <Btn
                    variant="danger"
                    size="sm"
                    loading={busy === 'archive'}
                    onClick={() => run('archive', () => hr.admin.people.archive(person.id), (r) => { setConfirmArchive(false); onArchived(r.person); })}
                  >
                    {`Archive ${name}`}
                  </Btn>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="hrp-ret__sec hrp-ret__hold">
        <div className="hrp-ret__holdtxt">
          <b id={`${ids}-hold`}><FiLock aria-hidden="true" /> Legal hold</b>
          <span id={`${ids}-holddesc`}>Blocks permanent deletion while a claim or investigation is open.</span>
        </div>
        <button
          type="button"
          role="switch"
          className="hrp-switch"
          aria-checked={!!person.legalHold}
          aria-labelledby={`${ids}-hold`}
          aria-describedby={`${ids}-holddesc`}
          disabled={busy === 'hold'}
          onClick={() => run('hold', () => hr.admin.people.legalHold(person.id, { on: !person.legalHold }), (r) => onHold(r.person, !person.legalHold))}
        >
          <span className="hrp-switch__knob" aria-hidden="true" />
        </button>
      </div>

      {canPurge && (
        <div className="hrp-ret__sec hrp-ret__purge">
          <p className="hrp-ret__txt">
            {`Retention ended ${fullDate(person.retainUntil)}. Only the HR owner can delete ${name} permanently; their change log is kept.`}
          </p>
          {!deleting ? (
            <Btn variant="danger" size="sm" icon={FiTrash2} onClick={() => setDeleting(true)}>Delete permanently</Btn>
          ) : (
            <div className="hrp-ret__typed">
              <Field label={`Type ${name} to confirm`}>
                <input ref={typedRef} className="hrp-input" type="text" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
              </Field>
              <div className="hrp-tl__btns">
                <Btn variant="ghost" size="sm" onClick={() => { setDeleting(false); setTyped(''); }} disabled={busy === 'purge'}>Keep</Btn>
                <Btn
                  variant="danger"
                  size="sm"
                  icon={FiTrash2}
                  loading={busy === 'purge'}
                  disabled={typed.trim() !== name}
                  onClick={() => run('purge', () => hr.admin.people.remove(person.id, { confirmName: typed.trim() }), () => onDeleted(name))}
                >
                  {`Delete ${name} permanently`}
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}
      {ownerNote && <p className="hrp-ret__owner">{ownerNote}</p>}
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </Card>
  );
}

export function ProfileRecord({ data, me, people, today, onToast, onReload, onPatchPerson, onDeleted }) {
  const person = data.person;
  const [offer, setOffer] = useState(false);
  // null until the list arrives, so the manager picker doesn't call anyone "no longer here" early.
  const allPeople = (people.data && people.data.people) || null;

  const saved = (result) => {
    onToast('Record saved');
    if (result.offerOffboarding) setOffer(true);
    onReload();
    people.reload();
  };

  return (
    <>
      {(offer || data.rehireRestoreHours > 0) && (
        <div className="hrp-stack hrp-rec__prompts">
          {data.rehireRestoreHours > 0 && (
            <RehirePrompt
              person={person}
              hours={data.rehireRestoreHours}
              onRestored={(hours) => { onToast(`Restored ${hours} hours of Sick & Safe`); onReload(); people.reload(); }}
            />
          )}
          {offer && (
            <OffboardingOffer
              person={person}
              onDismiss={() => setOffer(false)}
              onStarted={() => { setOffer(false); onToast('Offboarding checklist started'); onReload(); }}
            />
          )}
        </div>
      )}
      <div className="hrp-pf-cols hrp-pf-cols--rec">
        <div className="hrp-stack">
          <RecordForm person={person} people={allPeople} onSaved={saved} />
        </div>
        <div className="hrp-stack">
          <RetentionCard
            person={person}
            me={me}
            today={today}
            onArchived={(next) => { onToast(`${fullName(person)} archived`); if (next) onPatchPerson(next); onReload(); people.reload(); }}
            onHold={(next, on) => { onToast(on ? 'Legal hold is on' : 'Legal hold is off'); onPatchPerson(next || { legalHold: on }); }}
            onDeleted={onDeleted}
          />
        </div>
      </div>
    </>
  );
}

export default ProfileRecord;
